import { config } from "@/config";
export const HSA_FSA_HERO = {
  eyebrow: "now available",
  /* Figma forces this break; kept explicit so a fallback font cannot move it. */
  headline: ["Use your HSA/FSA", "funds on Matic"],
  subhead: "For qualifying customers through Flex.",
  ctaLabel: "shop now",
  mediaAlt:
    "A woman lies on a rug laughing beside Matic and a golden retriever",
};

export const HSA_FSA_PAYMENTS = {
  headline: "We accept all major HSA/FSA Cards",
  cards: [
    {
      id: "save",
      icon: "piggy-bank",
      title: "Save up to 40%",
      body: "Use HSA/FSA funds on Matic and essential accessories to pay with pre-tax dollars. Savings depend on your tax rate.",
    },
    {
      id: "deadline",
      icon: "timer",
      title: "Use your funds while you can",
      body: "FSA funds are often use-it-or-lose-it. Check your plan deadline and use any remaining balance before it expires.",
    },
  ],
} as const;

export const HSA_FSA_STEPS = {
  headline: "How to use your HSA/FSA dollars",
  steps: [
    {
      id: "select",
      label: "step 1",
      title: "Select pay with Flex",
      body: "Select Flex | Pay with HSA/FSA as your payment option at checkout.",
      mediaAlt: "",
      mediaFirst: true,
    },
    {
      id: "eligibility",
      label: "step 2",
      title: "Confirm your eligibility",
      body: "Complete a brief chat-based telehealth consultation to confirm your eligibility for HSA/FSA payment.",
      mediaAlt: "",
      mediaFirst: false,
      ctaLabel: "See how it works",
    },
    {
      id: "save",
      label: "step 3",
      title: "Save using pre-tax dollars",
      body: "If you qualify, pay with your HSA/FSA card or use a credit/debit card and submit for reimbursement. \n\nAfter payment, Flex will email you a Letter of Medical Necessity and an itemized receipt",
      mediaAlt: "",
      mediaFirst: true,
    },
  ],
  explainer: {
    dialogLabel: "How paying with HSA/FSA works",
    closeLabel: "Close video",
    mediaAlt:
      "A screen recording of the Flex checkout: the cart flags which items are HSA/FSA eligible, then a short chat collects consent and a phone number to start the consultation.",
    videoSrc: `${config.media.baseUrl}/videos/hsa-fsa-explainer.mp4`,
  },
} as const;

export const HSA_FSA_DATES = {
  headline: "Use your FSA funds before your deadline",
  subhead: "FSA deadlines vary by plan. Here are two dates to know:",
  dates: [
    {
      id: "december",
      month: "DEC",
      day: "31",
      /* Explicit lines and bold runs: Chrome ignores Figma's U+2028 breaks. */
      caption: [
        [{ text: "Most FSAs reset ", bold: true }, { text: "at the " }],
        [{ text: "end of the calendar\u00a0year." }],
      ],
    },
    {
      id: "march",
      month: "MAR",
      day: "15",
      caption: [
        [
          { text: "Some offer a grace period ", bold: true },
          { text: " through March 15." },
        ],
      ],
    },
  ],
} as const;

export const HSA_FSA_CTA = {
  headline: "Ready for Matic?",
  ctaLabel: "Shop now",
  mediaAlt: "",
} as const;

export const HSA_FSA_FAQS = {
  headline: "Frequently asked questions",
  subheadLead:
    "For more information, check out our FAQs below or reach out to ",
  subheadEmail: "hello@maticrobots.com",
  items: [
    {
      id: "partner",
      question: "Why did Matic partner with Flex?",
      answer:
        "Keeping floors clean can be physically demanding, especially for people dealing with chronic pain, limited mobility, pregnancy, respiratory conditions, or other issues that can make frequent vacuuming and mopping harder to manage. Bending, pushing a vacuum, and stirring up dust all make the chore more difficult.\n\nMatic vacuums and mops automatically, helping reduce the bending, pushing, and exposure to dust involved in keeping floors clean. We partnered with Flex to make Matic more accessible to qualifying customers who can use HSA/FSA funds toward their purchase.",
    },
    {
      id: "conditions",
      question: "Which conditions may qualify for HSA/FSA use with Matic?",
      answer:
        "You may qualify to use HSA/FSA funds for Matic if you experience:",
      bullets: [
        "Allergic rhinitis due to animal (cat/dog) hair and dander, pollen, or unspecified causes",
        "Asthma",
        "Atopic dermatitis",
        "COPD",
        "Chronic pain",
        "Chronic sinusitis",
        "Fibromyalgia",
        "Hypersensitivity pneumonitis",
        "Hypotonia (low muscle tone)",
        "Lower back pain",
        "Osteoarthritis",
        "Osteoporosis",
        "Paraplegia",
        "Parkinson’s disease",
        "Partial paralysis or hemiplegia",
        "Reduced mobility",
        "Rheumatoid arthritis",
      ],
    },
    {
      id: "what-is-flex",
      question: "What is Flex and what is their relationship with Matic?",
      answer:
        "Flex is Matic’s HSA/FSA payment partner. At checkout, Flex helps determine whether you qualify, facilitates the Letter of Medical Necessity process when required, and lets eligible customers pay for Matic and eligible accessories using HSA/FSA funds.",
    },
    {
      id: "how-to-pay",
      question: "How do I pay with my HSA or FSA card?",
      answer:
        "To use your HSA or FSA debit card, add products to your cart as usual.  At checkout, select “Flex | Pay with HSA/FSA” as your payment option,  enter your HSA or FSA debit card, and follow the remaining steps to complete checkout.  If you don’t see “Flex | Pay with HSA/FSA,” you may be in Shop Pay.  Select “checkout as guest” to view more payment options.",
    },
    {
      id: "no-card",
      question: "What if I don’t have my HSA/FSA card available?",
      answer:
        "If you don’t have your HSA or FSA card handy, still select “Flex | Pay  with HSA/FSA” as your payment method. Enter your credit card information and Flex will email you an itemized receipt to submit for  reimbursement.",
    },
    {
      id: "declined",
      question: "Why is my HSA/FSA card being declined?",
      answer:
        "HSA/FSA cards are debit cards, and the most common reason for declines  is insufficient funds. Reach out to your HSA/FSA administrator to  confirm your balance.",
    },
    {
      id: "multiple-cards",
      question:
        "I would like to use multiple HSA/FSA cards to pay for an item. Can I do that?",
      answer:
        "Unfortunately, this isn’t a supported feature right now. If there are insufficient funds in a single HSA or FSA account, you can instead  enter a credit card on the Flex checkout page. You will receive an  itemized receipt and/or Letter of Medical Necessity from Flex, which you can submit for reimbursement.",
    },
  ],
} as const;
